import React, { useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { commitLast, convertAll } from "../lib/translit";
import { api } from "../lib/api";

type Props = {
  value: string;
  onChange: (v: string) => void;
  className?: string;
  placeholder?: string;
  testid?: string;
  autoFocus?: boolean;
};

/**
 * Text input with automatic Bangla typing.
 * While typing, Avro-phonetic converts English → বাংলা instantly (on space).
 * On blur, an AI pass auto-corrects misspelled Banglish to the proper Bangla name
 * (e.g. "tarqk" → "তারেক"). Tap বাং/EN to switch a field to plain English.
 */
export default function BanglaInput({ value, onChange, className = "inp", placeholder, testid, autoFocus }: Props) {
  const [on, setOn] = useState(() => localStorage.getItem("re_bangla_type") !== "0");
  const [busy, setBusy] = useState(false);
  const lastSent = useRef("");

  const toggle = () => {
    const nv = !on;
    setOn(nv);
    localStorage.setItem("re_bangla_type", nv ? "1" : "0");
  };

  const handleChange = (raw: string) => {
    if (on && raw.endsWith(" ") && !value.endsWith(" ")) {
      onChange(commitLast(raw.trimEnd()) + " ");
    } else {
      onChange(raw);
    }
  };

  const aiFix = async (banglish: string) => {
    if (!banglish || banglish === lastSent.current) return;
    lastSent.current = banglish;
    setBusy(true);
    try {
      const { data } = await api.post("/translit", { text: banglish });
      if (data?.bangla) onChange(data.bangla);
    } catch {
      /* keep avro output on failure */
    } finally {
      setBusy(false);
    }
  };

  const handleBlur = () => {
    if (!on) return;
    const v = value.trim();
    if (!v || !/[a-zA-Z]/.test(v)) return; // already Bangla / empty → nothing to fix
    const avro = convertAll(value);
    if (avro !== value) onChange(avro); // instant fallback
    aiFix(v); // AI auto-correct from the original Banglish
  };

  return (
    <div className="relative">
      <input
        className={`${className} pr-12`}
        value={value}
        placeholder={placeholder}
        data-testid={testid}
        autoFocus={autoFocus}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={handleBlur}
      />
      {busy ? (
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-emerald-600" data-testid={testid ? `${testid}-ai-busy` : "bangla-ai-busy"}>
          <Loader2 size={16} className="animate-spin" />
        </span>
      ) : (
        <button
          type="button"
          onClick={toggle}
          tabIndex={-1}
          title={on ? "অটো বাংলা চালু — চাপলে ইংরেজি" : "ইংরেজি — চাপলে অটো বাংলা"}
          className={`absolute right-1.5 top-1/2 -translate-y-1/2 h-7 min-w-[34px] px-1.5 rounded-md text-[11px] font-bold transition-colors ${
            on ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-600"
          }`}
          data-testid={testid ? `${testid}-lang-toggle` : "bangla-toggle"}
        >
          {on ? "বাং" : "EN"}
        </button>
      )}
    </div>
  );
}
